import React, { useEffect, useState, useRef, useCallback } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const POLLING_INTERVAL = 15000;

// Reusable Status Badge (same as DevicesListPage)
const StatusBadge = ({ status, type = "status" }) => {
  const normalized = (status || "").toLowerCase();
  let style = {
    padding: "6px 12px",
    borderRadius: "12px",
    fontWeight: 600,
    fontSize: "0.8rem",
    display: "inline-block",
    textTransform: "capitalize",
    color: "#fff",
    boxShadow: "0 0 8px rgba(0,0,0,0.3)",
  };

  if (type === "approval") {
    style.background = normalized === "yes" || normalized === "1"
      ? "linear-gradient(135deg, #16A34A, #22C55E)"
      : "linear-gradient(135deg, #DC2626, #EF4444)";
    style.boxShadow = normalized === "yes" || normalized === "1"
      ? "0 0 10px rgba(34,197,94,0.5)"
      : "0 0 10px rgba(239,68,68,0.5)";
  } else {
    // agent status
    switch (normalized) {
      case "active":
        style.background = "linear-gradient(135deg, #16A34A, #22C55E)";
        style.boxShadow = "0 0 10px rgba(34,197,94,0.5)";
        break;
      case "suspended":
        style.background = "linear-gradient(135deg, #F59E0B, #FBBF24)";
        style.color = "#1E293B";
        style.boxShadow = "0 0 10px rgba(251,191,36,0.6)";
        break;
      case "sacked":
        style.background = "linear-gradient(135deg, #DC2626, #EF4444)";
        style.boxShadow = "0 0 10px rgba(239,68,68,0.5)";
        break;
      default:
        style.background = "linear-gradient(135deg, #9CA3AF, #D1D5DB)";
        style.color = "#1E293B";
        style.boxShadow = "0 0 10px rgba(156,163,175,0.5)";
    }
  }

  return <span style={style}>{status || "Unknown"}</span>;
};

function AdminAgentManagementPage() {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isPolling, setIsPolling] = useState(false);
  const [loadingActions, setLoadingActions] = useState({});
  const displayedAgentIds = useRef(new Set());
  const API_BASE_URL =
    process.env.REACT_APP_API_BASE_URL || "https://installmentzone.com/api";

  const fetchAgents = useCallback(async (isInitialFetch = false) => {
    if (isInitialFetch) setLoading(true);
    else setIsPolling(true);

    try {
      const response = await fetch(`${API_BASE_URL}/agents/agents.php`);
      const result = await response.json();
      if (result.success) {
        const fetchedAgents = result.data || [];

        if (!isInitialFetch) {
          const newAgents = fetchedAgents.filter(
            (a) => !displayedAgentIds.current.has(a.user_id.toString())
          );
          if (newAgents.length > 0) {
            toast.info(
              newAgents.length === 1
                ? `New agent: ${newAgents[0].name}`
                : `${newAgents.length} new agents added`
            );
          }
        }

        fetchedAgents.forEach(a => displayedAgentIds.current.add(a.user_id.toString()));
        setAgents(fetchedAgents);
      } else {
        toast.error(result.message || "Failed to fetch agents.");
      }
    } catch (error) {
      toast.error("Error fetching agents.");
    } finally {
      setLoading(false);
      setIsPolling(false);
    }
  }, [API_BASE_URL]);

  const handleAction = async (userId, actionType, endpoint, payload, successMsg, optimisticFn) => {
    const key = `${userId}_${actionType}`;
    setLoadingActions(prev => ({ ...prev, [key]: true }));

    let rollbackState = null;
    if (optimisticFn) {
      rollbackState = [...agents];
      setAgents(optimisticFn);
    }

    try {
      const res = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.success) {
        toast.success(data.message || successMsg);
      } else {
        throw new Error(data.message);
      }
    } catch (err) {
      toast.error(err.message || `Failed to ${actionType}`);
      if (rollbackState) setAgents(rollbackState);
    } finally {
      setLoadingActions(prev => ({ ...prev, [key]: false }));
    }
  };

  const updateApproval = (userId, approved) => {
    const optimisticFn = (prev) =>
      prev.map(a => a.user_id === userId ? { ...a, approved } : a);

    handleAction(
      userId,
      approved ? "approve" : "unapprove",
      "/agents/approve_agent.php",
      { user_id: userId, approved: Number(approved) },
      "Approval updated.",
      optimisticFn
    );
  };

  const updateStatus = (userId, status) => {
    const map = {
      suspended: "/agents/suspend_agent.php",
      active: "/agents/reactivate_agent.php",
      sacked: "/agents/sack_agent.php",
      reemploy: "/agents/reemploy_agent.php",
    };
    handleAction(
      userId,
      status,
      map[status],
      { user_id: userId },
      "Status updated."
    );
  };

  useEffect(() => {
    fetchAgents(true);
    const interval = setInterval(() => !isPolling && fetchAgents(), POLLING_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchAgents, isPolling]);

  return (
    <div
      style={{
        minHeight: "100vh",
        padding: "40px 20px",
        background: "linear-gradient(135deg, #1E3A8A, #3B82F6, #60A5FA)",
        fontFamily: "Segoe UI, sans-serif",
        color: "#fff",
        overflowY: "auto",
      }}
    >
      <ToastContainer theme="dark" />

      <div
        style={{
          background: "rgba(255,255,255,0.08)",
          padding: "30px",
          borderRadius: "15px",
          maxWidth: "1000px",
          margin: "0 auto",
          boxShadow: "0 6px 18px rgba(0,0,0,0.3)",
          backdropFilter: "blur(8px)",
        }}
      >
        <div style={{ textAlign: "center", marginBottom: "25px" }}>
          <h1 style={{ fontSize: "28px", fontWeight: "600" }}>
            Agent Management
          </h1>
          <p style={{ color: "#E0E7FF" }}>
            Approve and manage agent lifecycle.
          </p>
          {isPolling && (
            <small style={{ color: "#93C5FD" }}>Checking for updates...</small>
          )}
        </div>

        <div
          style={{
            overflowX: "auto",
            background: "rgba(255,255,255,0.9)",
            borderRadius: "10px",
          }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              color: "#1E293B",
            }}
          >
            <thead>
              <tr style={{ background: "#1E3A8A", color: "#fff" }}>
                <th style={{ padding: "14px", textAlign: "left" }}>ID</th>
                <th style={{ padding: "14px", textAlign: "left" }}>Name</th>
                <th style={{ padding: "14px", textAlign: "left" }}>Status</th>
                <th style={{ padding: "14px", textAlign: "left" }}>Approved</th>
                <th style={{ padding: "14px", textAlign: "left" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i}>
                    {[...Array(5)].map((__, j) => (
                      <td key={j} style={{ padding: "12px", background: "#F1F5F9" }}>
                        <div
                          style={{
                            height: "14px",
                            background: "#CBD5E1",
                            borderRadius: "4px",
                            width: "70%",
                            margin: "auto",
                          }}
                        />
                      </td>
                    ))}
                  </tr>
                ))
              ) : agents.length === 0 ? (
                <tr>
                  <td colSpan="5" style={{ textAlign: "center", padding: "20px", color: "#475569" }}>
                    No agents found.
                  </td>
                </tr>
              ) : (
                agents.map((agent, i) => {
                  const isApproved = Number(agent.approved) === 1;
                  const userIdStr = agent.user_id.toString();

                  return (
                    <tr
                      key={agent.user_id}
                      style={{
                        background: i % 2 === 0 ? "#F8FAFC" : "#FFFFFF",
                        borderBottom: "1px solid #E5E7EB",
                      }}
                    >
                      <td style={{ padding: "12px" }}>{agent.user_id}</td>
                      <td style={{ padding: "12px" }}>{agent.name || "N/A"}</td>
                      <td style={{ padding: "12px" }}>
                        <StatusBadge status={agent.status} />
                      </td>
                      <td style={{ padding: "12px" }}>
                        <StatusBadge status={isApproved ? "Yes" : "No"} type="approval" />
                      </td>
                      <td style={{ padding: "12px" }}>
                        {/* Approval */}
                        {!isApproved && (
                          <button
                            onClick={() => updateApproval(agent.user_id, 1)}
                            style={{
                              background: "#16A34A",
                              color: "#fff",
                              border: "none",
                              borderRadius: "6px",
                              padding: "6px 10px",
                              marginRight: "6px",
                              cursor: loadingActions[`${userIdStr}_approve`] ? "not-allowed" : "pointer",
                              opacity: loadingActions[`${userIdStr}_approve`] ? 0.7 : 1,
                            }}
                            disabled={loadingActions[`${userIdStr}_approve`]}
                          >
                            {loadingActions[`${userIdStr}_approve`] ? "..." : "Approve"}
                          </button>
                        )}
                        {isApproved && (
                          <button
                            onClick={() => updateApproval(agent.user_id, 0)}
                            style={{
                              background: "#DC2626",
                              color: "#fff",
                              border: "none",
                              borderRadius: "6px",
                              padding: "6px 10px",
                              marginRight: "6px",
                              cursor: loadingActions[`${userIdStr}_unapprove`] ? "not-allowed" : "pointer",
                              opacity: loadingActions[`${userIdStr}_unapprove`] ? 0.7 : 1,
                            }}
                            disabled={loadingActions[`${userIdStr}_unapprove`]}
                          >
                            {loadingActions[`${userIdStr}_unapprove`] ? "..." : "Unapprove"}
                          </button>
                        )}

                        {/* Status Actions */}
                        {agent.status === "active" && (
                          <button
                            onClick={() => updateStatus(agent.user_id, "suspended")}
                            style={{
                              background: "#F59E0B",
                              color: "#fff",
                              border: "none",
                              borderRadius: "6px",
                              padding: "6px 10px",
                              marginRight: "6px",
                              cursor: loadingActions[`${userIdStr}_suspend`] ? "not-allowed" : "pointer",
                              opacity: loadingActions[`${userIdStr}_suspend`] ? 0.7 : 1,
                            }}
                            disabled={loadingActions[`${userIdStr}_suspend`]}
                          >
                            Suspend
                          </button>
                        )}
                        {agent.status === "suspended" && (
                          <button
                            onClick={() => updateStatus(agent.user_id, "active")}
                            style={{
                              background: "#16A34A",
                              color: "#fff",
                              border: "none",
                              borderRadius: "6px",
                              padding: "6px 10px",
                              marginRight: "6px",
                              cursor: loadingActions[`${userIdStr}_reactivate`] ? "not-allowed" : "pointer",
                              opacity: loadingActions[`${userIdStr}_reactivate`] ? 0.7 : 1,
                            }}
                            disabled={loadingActions[`${userIdStr}_reactivate`]}
                          >
                            Reactivate
                          </button>
                        )}
                        {agent.status !== "sacked" && (
                          <button
                            onClick={() => updateStatus(agent.user_id, "sacked")}
                            style={{
                              background: "#DC2626",
                              color: "#fff",
                              border: "none",
                              borderRadius: "6px",
                              padding: "6px 10px",
                              marginRight: "6px",
                              cursor: loadingActions[`${userIdStr}_sack`] ? "not-allowed" : "pointer",
                              opacity: loadingActions[`${userIdStr}_sack`] ? 0.7 : 1,
                            }}
                            disabled={loadingActions[`${userIdStr}_sack`]}
                          >
                            Sack
                          </button>
                        )}
                        {agent.status === "sacked" && (
                          <button
                            onClick={() => updateStatus(agent.user_id, "reemploy")}
                            style={{
                              background: "#3B82F6",
                              color: "#fff",
                              border: "none",
                              borderRadius: "6px",
                              padding: "6px 10px",
                              cursor: loadingActions[`${userIdStr}_reemploy`] ? "not-allowed" : "pointer",
                              opacity: loadingActions[`${userIdStr}_reemploy`] ? 0.7 : 1,
                            }}
                            disabled={loadingActions[`${userIdStr}_reemploy`]}
                          >
                            Re-employ
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default AdminAgentManagementPage;